import React, {
  useContext, useEffect, useReducer, useState,
} from 'react';
import { Redirect, useParams } from 'react-router-dom';
import { Button } from '@mui/material';
import dayjs from 'dayjs';
import {
  deleteCoupon, getCoupon, getCouponView, updateCoupon,
} from 'features/coupon/api/coupons';
import { applyCoupon } from 'shared/api/subscription';
import { modelMap } from 'shared/utils';
import { BUTTON_COLORS } from 'shared/constants';
import { SideNavContext } from 'shared/hoc/withSideNav';
import { useNotification, useRequest } from 'shared/hooks';
import { InfoModal, Title } from 'shared/components/abstract/interactables';
import { dataReducer, DataReducerActions, initialDataReducerState } from 'shared/reducers';
import { getCouponPath, viewPath } from 'shared/routes/coupon';
import CouponInfo from './CouponInfo';
import { discountDurationEnums, discountTypeEnums } from './helper';

// CouponGetter makes an api request to pull all the info that <CouponInfo> needs.
// If the coupon doesn't exist, it redirects the user back to the coupon home page.
export default function CouponGetter() {
  const { couponId } = useParams();
  const { request, isLoading } = useRequest();
  const { pushInfo, pushError } = useNotification();
  const syncSideNav = useContext(SideNavContext);
  const [state, dispatch] = useReducer(dataReducer, initialDataReducerState);
  const [updateCount, setUpdateCount] = useState(0);

  const forceUpdate = () => setUpdateCount(updateCount + 1);
  const { data, updatedData } = state;

  useEffect(() => {
    (async () => {
      const [stripeCoupon, couponResult] = await Promise.all([
        request(getCoupon(couponId)), // Stripe coupon object.
        request(getCouponView(couponId)), // Internal data for coupon.
      ]);

      if (stripeCoupon === undefined || couponResult === undefined) return;

      if (!stripeCoupon || !couponResult) {
        dispatch({ action: DataReducerActions.set, payload: null });
        return;
      }

      const { ruleType } = couponResult;

      const {
        amount_off: amountOff,
        duration,
        duration_in_months: durationInMonths,
        max_redemptions: maxRedemption,
        percent_off: percentOff,
        redeem_by: redeemBy,
        times_redeemed: timesRedeemed,
      } = stripeCoupon;

      const rules = [];

      if (ruleType !== 'global' && ruleType !== 'tenant') {
        const model = modelMap[ruleType];
        const names = couponResult[model.nameKey].split(',');
        const ids = couponResult[model.idKey].split(',');
        ids.forEach((id, i) => {
          rules.push({ id, name: names[i] });
        });
      }

      const couponData = {
        ...couponResult,
        amountOff,
        discountType: amountOff ? discountTypeEnums.amountOff : discountTypeEnums.percentOff,
        duration: duration === 'once' ? discountDurationEnums.once : discountDurationEnums.repeating,
        durationInMonths,
        maxRedemption,
        percentOff,
        redeemBy: redeemBy ? dayjs.unix(redeemBy) : null,
        redemptionType: maxRedemption ? 'custom' : 'infinite',
        timesRedeemed,
      };

      dispatch({
        action: DataReducerActions.set,
        payload: {
          coupon: couponData,
          couponRuleType: ruleType,
          couponRules: rules,
          isExpired: redeemBy > 0 && redeemBy < Math.floor(Date.now() / 1000),
          isFullyRedeemed: maxRedemption && maxRedemption === timesRedeemed,
        },
      });
    })();
  }, [request, couponId, updateCount]);

  useEffect(() => {
    if (!data) return;
    if (data.isFullyRedeemed) {
      pushInfo('Coupon is fully redeemed.');
    } else if (data.isExpired) {
      pushError('Coupon is expired.');
    }
    if (data.coupon) {
      syncSideNav(getCouponPath(data.coupon.couponId), data.coupon.couponId);
    }
  }, [data, syncSideNav, pushInfo, pushError]);

  if (data === undefined) return null;
  if (data === null) return <Redirect to={viewPath} />;

  const {
    coupon,
    couponRuleType,
    couponRules,
    isExpired,
    isFullyRedeemed,
  } = data;

  const {
    tenantId,
    tenantName,
    timesRedeemed,
  } = coupon;

  const onDescriptionUpdate = async () => {
    const result = await request(updateCoupon(couponId, updatedData.description), 'Saved description');
    if (!result) return null;
    forceUpdate();
    return result;
  };

  const onCouponDelete = async () => {
    const result = await request(deleteCoupon(couponId), `deleted coupon "${couponId}"`);
    if (!result) return;
    syncSideNav(getCouponPath(data.coupon.couponId));
    dispatch({ action: DataReducerActions.set, payload: null });
  };

  const onCouponApply = async () => {
    const result = await request(applyCoupon(tenantId, couponId));
    if (!result) return;
    forceUpdate();
  };

  return (
    <>
      <Title title={couponId} />
      <CouponInfo
        coupon={coupon}
        couponId={couponId}
        couponIsCreated
        couponRules={couponRules}
        ruleType={couponRuleType}
        saveChanges={onDescriptionUpdate}
        tenantId={tenantId}
        tenantName={tenantName}
        updateValue={(key, value) => dispatch({
          action: DataReducerActions.update,
          payload: { key, value },
        })}
        updatedData={updatedData}
      >
        <>
          <InfoModal
            actionCallback={onCouponDelete}
            actionName="Delete Coupon"
            description={<p>Do not delete a coupon that has already been sent out!</p>}
            disabled={isLoading || timesRedeemed > 0}
            color={BUTTON_COLORS.error}
          />
          { (tenantId > 0 && !isExpired && !isFullyRedeemed) && (
          <Button
            sx={{ m: '5px' }}
            variant="contained"
            onClick={onCouponApply}
            disabled={isLoading}
          >
            Apply Coupon
          </Button>
          )}
        </>
      </CouponInfo>
    </>
  );
}
